import { getAccessToken, setAccessToken, clearAccessToken, refreshAccessToken } from '../auth/tokenStore';
import React, { createContext, useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api`; 

// Enable sending cookies
axios.defaults.withCredentials = true;

export const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [loading, setLoading] = useState(true);
  // View mode for admins - 'admin' or 'user'
  // Allows admins to switch to user view to see what their users see
  const [viewMode, setViewMode] = useState('admin');

  // User state - only essential fields needed globally
  // Other user data (rates, personal details) should be fetched on-demand by components
  const [user, setUser] = useState({
    // Identity
    id: null,
    username: null,
    email: null,
    firstName: null,
    lastName: null,
    profilePicture: null,
    // Authentication
    accessToken: null,
    // Role & Permissions
    role: null,
    isEcc: false,
    isAdmin: false,
    isCoordinator: false,
    isActive: false,
    isStaff: false,
    isCompanyAdmin: false,
    isSuperuser: false,
    inspector: false,
    valid_inspector_qualification: false,
    company_require_inspector_certificate: false,
    // HSE forced-ack (blocking read-&-sign sheet on mobile)
    company_force_safety_alert_ack: false,
    company_force_toolbox_ack: false,
    company_safety_alert_ack_min_severity: 'high',
    // Company Role
    companyRole: null,
    menuPermissions: [],
    // Sidebar preferences (user's own hidden/order settings)
    sidebarPreferences: {},
    // Company
    company: null,
    company_can_inspection: false,
    ppe_table_font_size: 'compact',
    // Timesheet mode
    useTaskBasedTimesheet: false,
    // Invoice system
    invoice: false,
    // Timesheet v2 feature flags
    tsEnableDrafts: false,
    // PPE color settings
    ppe_inspection_expired_color: null,
    ppe_inspection_first_check: null,
    ppe_inspection_first_check_color: null,
    ppe_inspection_second_check: null,
    ppe_inspection_second_check_color: null,
    ppe_manufacturer_expired_color: null,
    ppe_manufacturer_first_check: null,
    ppe_manufacturer_first_check_color: null,
    ppe_manufacturer_second_check: null,
    ppe_manufacturer_second_check_color: null,
  });

  const login = (userData) => {
    console.log("Login called for user:", {
      id: userData?.id,
      email: userData?.email,
      accessToken: userData?.accessToken ? '[REDACTED]' : null,
    });
    setUser(userData);
    setAccessToken(userData.accessToken);
    localStorage.setItem('userData', JSON.stringify(userData));
    console.log("Saved to localStorage:", {
      access_token: getAccessToken() ? '[present]' : null,
      userData_email: (() => {
        try { return JSON.parse(localStorage.getItem('userData') || '{}').email || null; }
        catch { return null; }
      })(),
    });
  
    fetchAdditionalUserData(userData.accessToken, userData.id);
  };

  const logout = async () => {
    console.warn("🔴 Logging out user!");
    try {
      await axios.post(`${API_BASE_URL}/logout/`, {}, {
        headers: { Authorization: `Bearer ${user.accessToken}` }
      });
      console.log("✅ Logout successful, refresh_token cookie removed.");
    } catch (error) {
      console.error("❌ Error during logout:", error.response?.data || error.message);
    }

    console.log("Clearing state and localStorage...");
    setUser({
      id: null,
      username: null,
      email: null,
      firstName: null,
      lastName: null,
      profilePicture: null,
      accessToken: null,
      role: null,
      isEcc: false,
      isAdmin: false,
      isCoordinator: false,
      isActive: false,
      isStaff: false,
      isCompanyAdmin: false,
      isSuperuser: false,
      inspector: false,
      valid_inspector_qualification: false,
      company_require_inspector_certificate: false,
      company_force_safety_alert_ack: false,
      company_force_toolbox_ack: false,
      company_safety_alert_ack_min_severity: 'high',
      companyRole: null,
      menuPermissions: [],
      sidebarPreferences: {},
      company: null,
      company_can_inspection: false,
      ppe_table_font_size: 'compact',
      useTaskBasedTimesheet: false,
      invoice: false,
      // PPE color settings
      ppe_inspection_expired_color: null,
      ppe_inspection_first_check: null,
      ppe_inspection_first_check_color: null,
      ppe_inspection_second_check: null,
      ppe_inspection_second_check_color: null,
      ppe_manufacturer_expired_color: null,
      ppe_manufacturer_first_check: null,
      ppe_manufacturer_first_check_color: null,
      ppe_manufacturer_second_check: null,
      ppe_manufacturer_second_check_color: null,
    });
    // Reset view mode to admin on logout
    setViewMode('admin');
    localStorage.removeItem('viewMode');
    clearAccessToken();
    localStorage.removeItem('userData');
  };

  // Switch view mode between 'admin' and 'user'
  // Only works for admin/adminves roles
  const switchViewMode = () => {
    const newMode = viewMode === 'admin' ? 'user' : 'admin';
    console.log(`Switching view mode from ${viewMode} to ${newMode}`);
    setViewMode(newMode);
    // Save to localStorage so apiService can read it
    localStorage.setItem('viewMode', newMode);
    return newMode;
  };

  // Check if current user can switch views (is admin)
  const canSwitchView = () => {
    return user?.role === 'admin' || user?.role === 'adminves';
  };

  // Update sidebar preferences in context (after saving to API)
  const setSidebarPreferences = (prefs) => {
    setUser(prev => ({ ...prev, sidebarPreferences: prefs }));
  };

  const fetchAdditionalUserData = async (token, userId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/users/${userId}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = response.data;
      // Extract only essential fields - other data should be fetched on-demand
      const essentialData = {
        id: data.id,
        username: data.username,
        email: data.email,
        firstName: data.first_name,
        lastName: data.last_name,
        profilePicture: data.profile_picture,
        // Signature (base64 PNG, no data-URL prefix). Already present in the
        // /users/{id}/ payload; kept here so the "Set Your Signature" preview and
        // Toolbox Talk signing see the stored signature after a reload/remount.
        user_signature: data.user_signature || null,
        company: data.company,
        companyName: data.company_name || '',
        company_can_inspection: data.company_can_inspection || false,
        ppe_table_font_size: data.ppe_table_font_size || 'compact',
        // Timesheet mode
        useTaskBasedTimesheet: data.use_task_based_timesheet || false,
        useTimesheetV2: data.use_timesheet_v2 || false,
        // Invoice system
        invoice: data.invoice || false,
        // Travel notification channels (per-company). Default TRUE when absent so behaviour
        // is unchanged until a company opts out. Used by the Assign modal to label the
        // primary action "Save & Send" vs "Save & Publish".
        sendTravelEmails: data.send_travel_emails !== false,
        sendTravelPush: data.send_travel_push !== false,
        // ETT (estimated travel time) opt-in. Default FALSE when absent — the feature is
        // off until a company explicitly enables it (Assign modal hides the ETT field).
        estimatedTravelTimeEnabled: data.estimated_travel_time_enabled === true,
        // ETT computation policy (plane trips + per-day cap). Defaults mirror the backend.
        ettAirportAccessMode: data.ett_airport_access_mode || 'fixed',
        ettFixedAirportMinutes: Number.isFinite(data.ett_fixed_airport_minutes) ? data.ett_fixed_airport_minutes : 120,
        ettMaxDailyMinutes: Number.isFinite(data.ett_max_daily_minutes) ? data.ett_max_daily_minutes : 0,
        // Timesheet v2 feature flags
        tsEnableDrafts: data.ts_enable_drafts || false,
        // Map snake_case flags to camelCase
        isCoordinator: data.is_coordinator || false,
        isEcc: data.is_ecc || false,
        isCompanyAdmin: data.is_company_admin || false,
        isStaff: data.is_staff || false,
        isSuperuser: data.is_superuser || false,
        isActive: data.is_active || false,
        inspector: data.inspector || false,
        valid_inspector_qualification: data.valid_inspector_qualification ?? false,
        company_require_inspector_certificate: data.company_require_inspector_certificate ?? false,
        // HSE forced-ack (blocking read-&-sign sheet on mobile)
        company_force_safety_alert_ack: data.company_force_safety_alert_ack ?? false,
        company_force_toolbox_ack: data.company_force_toolbox_ack ?? false,
        company_safety_alert_ack_min_severity: data.company_safety_alert_ack_min_severity || 'high',
        // Training roles (array of IDs)
        training_roles: data.training_roles || [],
        // Company Role
        companyRole: data.company_role_data || null,
        menuPermissions: data.company_role_data?.menu_permissions || [],
        // Sidebar preferences
        sidebarPreferences: data.sidebar_preferences || {},
        // PPE color settings
        ppe_inspection_expired_color: data.ppe_inspection_expired_color,
        ppe_inspection_first_check: data.ppe_inspection_first_check,
        ppe_inspection_first_check_color: data.ppe_inspection_first_check_color,
        ppe_inspection_second_check: data.ppe_inspection_second_check,
        ppe_inspection_second_check_color: data.ppe_inspection_second_check_color,
        ppe_manufacturer_expired_color: data.ppe_manufacturer_expired_color,
        ppe_manufacturer_first_check: data.ppe_manufacturer_first_check,
        ppe_manufacturer_first_check_color: data.ppe_manufacturer_first_check_color,
        ppe_manufacturer_second_check: data.ppe_manufacturer_second_check,
        ppe_manufacturer_second_check_color: data.ppe_manufacturer_second_check_color,
      };
      setUser((prevUser) => ({ ...prevUser, ...essentialData }));
      console.log("User essential data loaded:", essentialData);
    } catch (error) {
      console.error("Error fetching additional user data:", error);
    }
  };

  const updateUserContext = async () => {
    const token = getAccessToken();
    if (token && user.id) {
      try {
        await fetchAdditionalUserData(token, user.id);
        console.log("User context updated successfully.");
      } catch (error) {
        console.error("Error updating user context:", error);
      }
    } else {
      console.warn("Token not found. User context not updated.");
    }
  };

  useEffect(() => {
    console.log("=== [UserProvider] 🔄 Initializing user context ===");
    console.log("📌 LocalStorage - userData:", localStorage.getItem("userData") ? '[present]' : null);
    console.log("📌 LocalStorage - access_token:", getAccessToken() ? '[present]' : null);

    const initializeUser = async () => {
      try {
        let storedUserData = JSON.parse(localStorage.getItem("userData") || 'null');
        let accessToken = getAccessToken();

        // In-memory mode (production) after a reload: the access token is gone,
        // so mint a fresh one from the httpOnly refresh cookie BEFORE the app
        // renders (the provider gates rendering on `loading`).
        if (storedUserData && !accessToken) {
          accessToken = await refreshAccessToken();
        }

        if (storedUserData && accessToken) {
          console.log("✅ [UserProvider] User recognized, updating state.");
          setUser({ ...storedUserData, accessToken });
          await fetchAdditionalUserData(accessToken, storedUserData.id);
        } else {
          if (storedUserData && !accessToken) {
            // Had a session but the refresh failed → fully logged out.
            localStorage.removeItem("userData");
          }
          console.warn("⚠️ No active session - user is not logged in.");
        }
      } finally {
        setLoading(false);
      }
    };

    initializeUser();
  }, []);

  return (
    <UserContext.Provider value={{
      user,
      loading,
      login,
      logout,
      updateUserContext,
      // View mode switching for admins
      viewMode,
      switchViewMode,
      canSwitchView,
      setSidebarPreferences,
    }}>
      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#0f172a' }}>
          <div style={{ width: 32, height: 32, border: '2px solid #f97316', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      ) : children}
    </UserContext.Provider>
  );
};
